/**
 * The editor's extension list (`061-editor.md` §1).
 *
 * **The resulting schema must be EXACTLY the node and mark set enumerated in
 * `031-content-format.md`.** That table is the contract; this list is the implementation
 * of it. If they disagree, `031` wins and this file is wrong.
 *
 * The schema is also our primary XSS defence (`120-non-functional.md`): Tiptap renders
 * from JSON through the registered schema, and anything not defined here never enters a
 * document. That is why we never `dangerouslySetInnerHTML` server content, and why an
 * extension is not added here casually — an extra node type is an extra thing an attacker
 * can get into `page.content`.
 *
 * Do not add a package that is not in `020-tech-stack.md`. If you find you need one, that
 * is an ADR, not an `npm install`.
 */
import { Extension, type Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import Image from '@tiptap/extension-image';
import FileHandler from '@tiptap/extension-file-handler';
import { CharacterCount, Focus, Placeholder } from '@tiptap/extensions';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ALLOWED_MIME } from '@/api/endpoints/assets';
import { translate } from '@/lib/i18n';
import { ImageNodeView } from '@/features/editor/ImageNodeView';
import { promptForLink } from '@/features/editor/link';
import { Highlight, TextColor } from '@/features/editor/marks';
import { RpnSearch } from '@/features/editor/search';
import { WikiLink } from '@/features/editor/wikiLink';
import { transformPastedHTML } from '@/features/editor/paste/transformPastedHTML';
import {
  insertImages,
  shouldUploadPastedFiles,
  type UploadContext,
} from '@/features/editor/uploads';

/**
 * `031`'s `image` node.
 *
 * `allowBase64: false`: a base64 image in the document would bloat the row, break the
 * search projection and defeat asset dedupe. `src` is always a relative `/uploads/` path.
 *
 * `uploading`, `uploadId` and `failed` are declared `rendered: false` — they never reach the
 * DOM. They DO appear in `editor.getJSON()`. **`rendered: false` does NOT mean "not
 * serialized"**, and assuming it does is how a transient attr ends up in Postgres;
 * `stripTransientAttrs` is what actually keeps it out (`lib/content.ts`).
 */
export const RpnImage = Image.extend({
  name: 'image', // keep the node name; do NOT rename
  addAttributes() {
    return {
      ...this.parent?.(),
      assetId: { default: null },
      width: { default: null },
      height: { default: null },
      uploading: { default: false, rendered: false },
      uploadId: { default: null, rendered: false },
      failed: { default: false, rendered: false },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
}).configure({ allowBase64: false, inline: false });

/**
 * `110-design-system.md` §7: **`Mod+E` is the AI panel.** StarterKit's `Code` mark binds
 * inline code to `Mod+E` by default; that binding is explicitly removed and re-bound to
 * `Mod+Shift+C`. The AI panel is used a hundred times a day; inline code is not.
 *
 * A keymap Extension rather than `Code.extend()`, because Code arrives inside StarterKit
 * and re-registering it by name would be a duplicate-extension runtime error.
 */
const RpnKeymap = Extension.create({
  name: 'rpnKeymap',
  addKeyboardShortcuts() {
    return {
      'Mod-e': () => true, // swallow it — the AI panel claims this (M6). Never inline code.
      'Mod-Shift-c': () => this.editor.commands.toggleCode(),

      /**
       * `110` §7 → Editor: **"`Mod+Shift+X` — Strikethrough."**
       *
       * StarterKit's `Strike` mark binds `Mod-Shift-s` and nothing else, so the shortcut the
       * design system promises was simply not bound — the toolbar button worked and the key did
       * nothing. Caught by walking the `?` sheet row by row against the running app (M7,
       * criterion 3: "a stale help overlay is worse than none"). StarterKit's own binding is
       * left in place; it costs nothing and breaks no other key.
       */
      'Mod-Shift-x': () => this.editor.commands.toggleStrike(),

      /**
       * `110` §7: **"`Mod+K` — Insert link when there is a text selection; command palette when
       * there is not. The only overloaded binding, and it is the convention everywhere."**
       *
       * Returning `false` DECLINES the key, which is the whole mechanism: ProseMirror does not
       * `preventDefault` it, and — more to the point — the palette's own `window` listener asks
       * the identical question (`editorHasTextSelection`) and declines it in the other
       * direction. Two listeners on two layers, one predicate. See `features/editor/link.ts`.
       */
      'Mod-k': () => {
        if (this.editor.state.selection.empty) return false;
        promptForLink(this.editor);
        return true;
      },
    };
  },
});

export const extensions: Extensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] }, // 031: levels 1–3 only. More is noise in a dense UI.
    codeBlock: { languageClassPrefix: 'language-' },

    // Link comes FROM StarterKit. Do not also add `@tiptap/extension-link` — a duplicate
    // extension name is a runtime error in Tiptap v3, and it is the single most common
    // way to break the build here (`061` §1).
    link: {
      openOnClick: false, // clicking places the caret; Cmd+click opens
      autolink: true,
      defaultProtocol: 'https',
      protocols: ['http', 'https', 'mailto'],
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
    },
  }),

  TableKit.configure({
    table: { resizable: true, allowTableNodeSelection: true },
  }),

  TaskList,
  TaskItem.configure({ nested: true }),

  // `031`'s `highlight` and `textColor` marks. Both carry a single `color` attr constrained to
  // a palette key; the colour itself is a CSS token (`styles/theme.css`), so it is theme-aware
  // and no arbitrary value enters the document. See `marks.ts`.
  Highlight,
  TextColor,

  RpnImage,

  // `031`'s `wikiLink`, plus the `[[` suggester it is authored with (`070` §7.1). It goes in
  // THIS list — not in `editorExtensions()` below — because the schema must contain the node
  // for the paste fixtures to parse against it, and because the suggester needs nothing from
  // the page: it searches, and it inserts. It never creates a page.
  WikiLink,

  Placeholder.configure({
    // Context-aware, not a single string. Only the empty document and a focused empty
    // heading get one — a placeholder on every empty paragraph is visual noise, and this
    // app values density.
    placeholder: ({ node, editor }) => {
      if (editor.isEmpty) return translate('editor.placeholder.empty');
      if (node.type.name === 'heading') return translate('editor.placeholder.heading');
      return '';
    },
    showOnlyCurrent: true,
  }),

  // In-editor find & replace (`110` §7). A decoration-only extension — it adds NO node and NO
  // mark, so the schema (and the `getSchema(extensions)` paste corpus) is unchanged, and it is
  // safe in this shared list rather than being page-scoped. Custom, not a package — see the
  // header of this file and `search.ts`.
  RpnSearch,

  CharacterCount.configure({ limit: null }),

  // A subtle left-border tick on the focused block. That is all "focus mode" is here: a
  // visual anchor in a long page. There is no full-screen zen mode.
  Focus.configure({ className: 'has-focus', mode: 'shallowest' }),

  RpnKeymap,
];

/**
 * The editor's real extension list: the schema above, plus `FileHandler`, which needs the
 * page's `UploadContext` and therefore cannot be a module constant.
 *
 * **`extensions` above stays free of it on purpose.** `getSchema(extensions)` is what the
 * paste fixture corpus parses against (`061` §4.6), and a schema that could only be built
 * with a page id and a callback in hand would not be testable at all. `FileHandler` adds no
 * node and no mark, so the two lists produce the identical schema — which is the property
 * that makes this split safe.
 */
export function editorExtensions(ctx: UploadContext): Extensions {
  return [
    ...extensions,

    FileHandler.configure({
      allowedMimeTypes: [...ALLOWED_MIME],

      /* ────────────────────────────────────────────────────────────────────────
       * THE DOUBLE-INSERT TRAP (`061` §5). Read before changing anything here.
       *
       * A Word paste with an inline image puts BOTH an HTML fragment (with an
       * `<img>`) AND the image's bytes on the clipboard. Handle the files here and
       * ALSO let ProseMirror parse the HTML, and the image lands twice.
       *
       * `consumePasteEvent` is left at its default `false`, which makes the
       * extension return `false` from `handlePaste` whenever HTML is present — so
       * ProseMirror's default paste still runs on the sanitized HTML and the text
       * and the table survive. Setting it to `true` would cancel that paste and
       * throw the table away to keep the picture. Never do that.
       *
       * What is left for us to decide is only whether to upload the blobs as well,
       * and `shouldUploadPastedFiles` is that decision, extracted as a pure
       * function so the regression is a unit test rather than a bug report.
       * ──────────────────────────────────────────────────────────────────────── */
      onPaste: (editor, files, htmlContent) => {
        if (htmlContent !== undefined && htmlContent !== '') {
          if (!shouldUploadPastedFiles(transformPastedHTML(htmlContent), files.length)) return;

          // Deferred to the next tick so ProseMirror finishes its own paste transaction
          // first. Appending into a transaction that is still being applied is how you
          // corrupt a document.
          queueMicrotask(() => {
            void insertImages(editor, ctx, files, 'end');
          });
          return;
        }

        // No HTML at all → a genuine binary paste. A screenshot. We own it, and the
        // extension has already cancelled ProseMirror's default paste for us.
        void insertImages(editor, ctx, files, null);
      },

      onDrop: (editor, files, pos) => {
        void insertImages(editor, ctx, files, pos);
      },
    }),
  ];
}
